import {
  ArrowLeftOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
  DeleteOutlined,
  EditOutlined,
  ExpandAltOutlined,
  FileOutlined,
  FolderOutlined,
  HolderOutlined,
  HomeOutlined,
  ShareAltOutlined,
} from '@ant-design/icons'
import CloudUploadOutlined from '@ant-design/icons/lib/icons/CloudUploadOutlined'
import ExclamationCircleOutlined from '@ant-design/icons/lib/icons/ExclamationCircleOutlined'
import FolderAddOutlined from '@ant-design/icons/lib/icons/FolderAddOutlined'
import {
  Avatar,
  Breadcrumb,
  Button,
  Empty,
  Input,
  message,
  Modal,
  Space,
  Tooltip,
} from 'antd'
import debounce from 'lodash/debounce'
import { toJS } from 'mobx'
import { inject, observer } from 'mobx-react'
import queryString from 'query-string'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import { useHistory, useLocation } from 'react-router-dom'
import uuid from 'react-uuid'
import { blue } from '../../color'
import { subStringAvatar } from '../../components/Common/CellText'
import FileItem from '../../components/Common/FileItemComponent/FileItem'
import ContentBlockWrapper from '../../components/ContentBlockWrapper'
import PageTitle from '../../components/PageTitle'
import { apiUrl } from '../../config'
import { ACL_ACTION_TYPE } from '../../constants'
import date_format from '../../date_format'
import DashboardLayout from '../../layouts/DashboardLayout/DashboardLayout'
import utils from '../../utils'
import CreateMyFolderModal from './CreateMyFolder/CreateMyFolderModal'
import MoveDocumentModal from './MoveDocumentModal/MoveDocumentModal'
import moveDocumentStore from './MoveDocumentModal/moveDocumentStore'
import {
  ActionBtn,
  ActionCell,
  DateText,
  DragHandle,
  EmptyWrap,
  ExplorerToolbar,
  FileTable,
  FileTableWrapper,
  InlineInput,
  NameCell,
  SearchBox,
  SectionTitle,
  SortableHeader,
  SortIcon,
} from './MyDocumentPageStyled'
import renameDocumentStore from './RenameDocumentModal/renameDocumentStore'
import ShareDocumentModal from './ShareDocumentModal/ShareDocumentModal'
import UploadMyFileModal from './UploadMyFile/UploadMyFileModal'

const { Search } = Input

const MyDocumentPage = props => {
  const history = useHistory()
  const location = useLocation()
  const queryStringParsed = queryString.parse(location.search, {
    arrayFormat: 'index',
  })

  const { resourcesStore, aclStore, userStore, authenticationStore } = props
  const { mentionUserList } = userStore
  const { currentUser } = authenticationStore

  const { items, shareList, breadCrumb, isLoading } = resourcesStore
  const { currentFolderId } = resourcesStore

  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false)
  const [showUploadFileModal, setShowUploadFileModal] = useState(false)
  const [showMoveDocumentModal, setShowMoveDocumentModal] = useState(false)
  const [showShareDocumentModal, setShowShareDocumentModal] = useState(false)
  const [currentDocument, setCurrentDocument] = useState(null)
  const [textKeyword, setTextKeyword] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [editingName, setEditingName] = useState('')
  const [editingExtension, setEditingExtension] = useState('')
  const [dragOverId, setDragOverId] = useState(null)
  const [sortField, setSortField] = useState('name')
  const [sortOrder, setSortOrder] = useState('asc')
  const renameInputRef = useRef(null)

  useEffect(() => {
    if (currentUser?.username && aclStore?.aclActionsByUser?.length === 0) {
      aclStore.getACLDetailByUser(currentUser?.username)
    }
  }, [currentUser?.username, aclStore?.aclActionsByUser?.length])

  useEffect(() => {
    if (queryStringParsed.id) {
      resourcesStore.currentFolderId = queryStringParsed.id
    }
    resourcesStore.search()
  }, [currentFolderId])

  useEffect(() => {
    resourcesStore.search()
    resourcesStore.searchShareList()
  }, [resourcesStore.keyword])

  const sortList = useCallback(
    list => {
      if (!list) return []

      return [...list].sort((a, b) => {
        const aIsFolder = a.type === 'folder'
        const bIsFolder = b.type === 'folder'
        if (aIsFolder && !bIsFolder) return -1
        if (!aIsFolder && bIsFolder) return 1

        let comparison = 0
        if (sortField === 'name') {
          comparison = (a.name || '').localeCompare(b.name || '', 'vi', {
            sensitivity: 'base',
          })
        } else if (sortField === 'updatedAt') {
          comparison = new Date(a.updatedAt) - new Date(b.updatedAt)
        }

        return sortOrder === 'asc' ? comparison : -comparison
      })
    },
    [sortField, sortOrder]
  )

  const sortedItems = useMemo(() => sortList(items), [items, sortList])
  const sortedShareList = useMemo(() => sortList(shareList), [
    shareList,
    sortList,
  ])
  const handleSort = useCallback(
    field => {
      if (sortField === field) {
        setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortField(field)
        setSortOrder('asc')
      }
    },
    [sortField]
  )

  const renderSortIcon = useCallback(
    field => {
      if (sortField !== field)
        return (
          <SortIcon>
            <CaretUpOutlined style={{ marginBottom: -4, fontSize: 8 }} />
            <CaretDownOutlined style={{ fontSize: 8 }} />
          </SortIcon>
        )
      return (
        <SortIcon active>
          {sortOrder === 'asc' ? <CaretUpOutlined /> : <CaretDownOutlined />}
        </SortIcon>
      )
    },
    [sortField, sortOrder]
  )

  const openFolder = useCallback(
    folderId => {
      resourcesStore.currentFolderId = folderId
      resourcesStore.keyword = null
      setTextKeyword(null)
      history.replace({ search: `?id=${folderId}` })
    },
    [history, resourcesStore]
  )

  const goBack = useCallback(() => {
    const parentId =
      breadCrumb?.length > 1 ? breadCrumb[breadCrumb.length - 2]?.id : ''
    openFolder(parentId)
  }, [breadCrumb, openFolder])

  const onSearch = useCallback(
    keyword => {
      resourcesStore.keyword = keyword
      resourcesStore.search()
    },
    [resourcesStore]
  )

  const debouncedSearch = useCallback(
    debounce(val => {
      resourcesStore.keyword = val
    }, 500),
    []
  )

  const isItemOwner = useCallback(
    record => {
      if (!record) return false
      const recordOwner =
        record.ownerUser || record.createdBy || record.created_by
      if (recordOwner && currentUser?.username) {
        return recordOwner === currentUser.username
      }
      return !currentFolderId
    },
    [currentUser?.username, currentFolderId]
  )

  /* handlers */
  const handleClickRename = useCallback(
    item => {
      if (!isItemOwner(item)) return
      setEditingId(item.id)
      if (item.type !== 'folder') {
        const lastDot = item.name.lastIndexOf('.')
        if (lastDot > 0) {
          setEditingName(item.name.substring(0, lastDot))
          setEditingExtension(item.name.substring(lastDot))
        } else {
          setEditingName(item.name)
          setEditingExtension('')
        }
      } else {
        setEditingName(item.name)
        setEditingExtension('')
      }
      setTimeout(() => renameInputRef.current?.focus(), 50)
    },
    [isItemOwner]
  )

  const handleRenameConfirm = useCallback(async () => {
    const trimmed = editingName.trim()
    if (!trimmed || trimmed === '') {
      setEditingId(null)
      return
    }
    const finalName = trimmed + editingExtension
    try {
      await renameDocumentStore.rename(finalName, editingId)
    } catch (e) {
      if (e?.errorCode === 'PERMISSION-100') {
        message.error('Không có quyền đổi tên ở đây')
      } else {
        message.error('Đã có lỗi xảy ra!')
      }
    } finally {
      setEditingId(null)
      setEditingExtension('')
      resourcesStore.search()
    }
  }, [editingName, editingExtension, editingId, resourcesStore])

  const handleRenameKeyDown = useCallback(
    e => {
      if (e.key === 'Enter') handleRenameConfirm()
      if (e.key === 'Escape') setEditingId(null)
    },
    [handleRenameConfirm]
  )

  const handleClickShare = useCallback(
    item => {
      if (!isItemOwner(item)) return
      setCurrentDocument(item)
      setShowShareDocumentModal(true)
    },
    [isItemOwner]
  )

  const handleClickMove = useCallback(
    item => {
      if (!isItemOwner(item)) return
      setCurrentDocument(item)
      setShowMoveDocumentModal(true)
    },
    [isItemOwner]
  )

  /* drag-and-drop handlers */
  const handleDragStart = useCallback((e, record) => {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', record.id)
    e.dataTransfer.setDragImage(e.currentTarget, 20, 20)
  }, [])

  const handleDragOver = useCallback((e, targetRecord) => {
    if (targetRecord.type !== 'folder') return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverId(targetRecord.id)
  }, [])

  const handleDragLeave = useCallback(() => {
    setDragOverId(null)
  }, [])

  const handleDrop = useCallback(
    async (e, targetRecord) => {
      e.preventDefault()
      setDragOverId(null)
      if (targetRecord.type !== 'folder') return
      const draggedId = e.dataTransfer.getData('text/plain')
      if (!draggedId || draggedId === targetRecord.id) return

      try {
        await moveDocumentStore.move(draggedId, targetRecord.id)
        message.success('Di chuyển thành công')
      } catch (err) {
        message.error(err?.vi || 'Đã có lỗi xảy ra!')
      }
    },
    [currentFolderId, moveDocumentStore]
  )

  const { confirm } = Modal

  const handleClickDelete = useCallback(
    (id, item) => {
      if (item && !isItemOwner(item)) return
      confirm({
        title: 'Bạn có chắc muốn xóa tài liệu này?',
        icon: <ExclamationCircleOutlined />,
        okText: 'Xóa',
        okType: 'danger',
        cancelText: 'Hủy',
        onOk: async () => {
          try {
            await resourcesStore.delete(id)
          } catch (e) {
            message.error(e.vi)
          } finally {
            resourcesStore.isLoading = false
          }
        },
      })
    },
    [confirm, resourcesStore, isItemOwner]
  )

  const handleClickPublish = useCallback(
    item => {
      if (!isItemOwner(item)) return
      confirm({
        title: `Bạn có chắc muốn ${
          item?.isPublished ? 'huỷ công khai' : 'công khai'
        } tài liệu này?`,
        icon: <ExclamationCircleOutlined />,
        okText: 'Đồng ý',
        okType: 'danger',
        cancelText: 'Hủy',
        onOk: async () => {
          try {
            await resourcesStore.published({
              ...item,
              isPublished: !item?.isPublished,
            })
          } catch (e) {
            message.error(e.vi)
          } finally {
            resourcesStore.isLoading = false
          }
        },
      })
    },
    [confirm, resourcesStore, isItemOwner]
  )

  /* shared-with avatars */
  const renderSharedAvatars = (sharedWith, ownerUser) => {
    if (!sharedWith || sharedWith.length === 0) return null
    let list = sharedWith.includes(ownerUser)
      ? sharedWith
      : [ownerUser, ...sharedWith]
    const uniq = Array.from(new Set(list))
    const visible = uniq.slice(0, 3)
    const rest = uniq.length - visible.length
    return (
      <Tooltip
        title={
          <div>
            {uniq.map(u => {
              const found = mentionUserList.find(m => m.username === u)
              return <div key={u}>{found?.display || u}</div>
            })}
          </div>
        }>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {visible.map(u => {
            const found = mentionUserList.find(m => m.username === u)
            return (
              <Avatar
                key={found?.username || uuid()}
                size={22}
                style={{
                  backgroundColor: blue,
                  fontSize: 11,
                  marginLeft: -4,
                  border: '1px solid #fff',
                }}
                src={found?.image && `${apiUrl}/api/v1/images/${found?.image}`}>
                {subStringAvatar(found?.display || found?.username || u)}
              </Avatar>
            )
          })}
          {rest > 0 && (
            <Avatar
              size={22}
              style={{
                backgroundColor: '#ccc',
                fontSize: 10,
                marginLeft: -4,
                border: '1px solid #fff',
              }}>
              +{rest}
            </Avatar>
          )}
        </div>
      </Tooltip>
    )
  }

  const renderBreadcrumb = useCallback(() => {
    if (!breadCrumb || breadCrumb.length === 0) {
      return (
        <Breadcrumb>
          <Breadcrumb.Item>
            <HomeOutlined />
            <span style={{ marginLeft: 4 }}>Tài liệu của tôi</span>
          </Breadcrumb.Item>
        </Breadcrumb>
      )
    }
    return (
      <Breadcrumb>
        {breadCrumb.map((item, index) => {
          const isLast = index === breadCrumb.length - 1
          return (
            <Breadcrumb.Item key={item.id ?? '__root__'}>
              {index === 0 ? (
                <span
                  style={{ cursor: isLast ? 'default' : 'pointer' }}
                  onClick={isLast ? undefined : () => openFolder(item.id)}>
                  <HomeOutlined />
                  <span style={{ marginLeft: 4 }}>{item.name}</span>
                </span>
              ) : (
                <span
                  style={{
                    cursor: isLast ? 'default' : 'pointer',
                    fontWeight: isLast ? 500 : undefined,
                  }}
                  onClick={isLast ? undefined : () => openFolder(item.id)}>
                  {item.name}
                </span>
              )}
            </Breadcrumb.Item>
          )
        })}
      </Breadcrumb>
    )
  }, [breadCrumb, openFolder])

  const canShare = aclStore.checkAccessControlAction(
    ACL_ACTION_TYPE.share__TAI_LIEU
  )
  const canRename = aclStore.checkAccessControlAction(
    ACL_ACTION_TYPE.rename__TAI_LIEU
  )
  const canMove = aclStore.checkAccessControlAction(
    ACL_ACTION_TYPE.move__TAI_LIEU
  )
  const canDelete = aclStore.checkAccessControlAction(
    ACL_ACTION_TYPE.delete__TAI_LIEU
  )
  const canCreate = aclStore.checkAccessControlAction(
    ACL_ACTION_TYPE.create__TAI_LIEU
  )
  const canUpload = aclStore.checkAccessControlAction(
    ACL_ACTION_TYPE.upload__TAI_LIEU
  )

  return (
    <>
      <DashboardLayout>
        <Helmet>
          <title>Tài liệu của tôi</title>
        </Helmet>
        <PageTitle location={location} customBreadcrumb={renderBreadcrumb()} />
        <ContentBlockWrapper
          style={{ minHeight: 'calc(100vh - 150px)', overflow: 'hidden' }}>
          <ExplorerToolbar
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 8,
              padding: '8px 16px',
            }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flex: '1 1 300px',
                minWidth: 0,
              }}>
              {currentFolderId && (
                <Tooltip title="Quay lại">
                  <Button icon={<ArrowLeftOutlined />} onClick={goBack} />
                </Tooltip>
              )}
              <SearchBox
                style={{ flex: '1 1 250px', maxWidth: 360, width: 'auto' }}>
                <Search
                  allowClear
                  enterButton={false}
                  placeholder="Tìm kiếm thư mục, tài liệu..."
                  onSearch={onSearch}
                  value={textKeyword}
                  onChange={e => {
                    const val = e.target.value
                    setTextKeyword(val)
                    debouncedSearch(val)
                  }}
                />
              </SearchBox>
            </div>
            <Space style={{ marginLeft: 'auto', flexShrink: 0 }}>
              {canCreate && (
                <Button
                  icon={<FolderAddOutlined />}
                  type="primary"
                  onClick={() => setShowCreateFolderModal(true)}>
                  Tạo thư mục
                </Button>
              )}
              {canUpload && (
                <Button
                  icon={<CloudUploadOutlined />}
                  onClick={() => setShowUploadFileModal(true)}>
                  Tải file lên
                </Button>
              )}
            </Space>
          </ExplorerToolbar>

          {/* Unified file list */}
          {isLoading ? (
            <EmptyWrap>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Đang tải..."
              />
            </EmptyWrap>
          ) : items?.length === 0 &&
            (currentFolderId || shareList?.length === 0) ? (
            <EmptyWrap>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Không có dữ liệu"
              />
            </EmptyWrap>
          ) : (
            <FileTableWrapper>
              <FileTable>
                <thead>
                  <tr>
                    <th
                      onClick={() => handleSort('name')}
                      style={{ minWidth: 220 }}>
                      <SortableHeader>
                        Tên {renderSortIcon('name')}
                      </SortableHeader>
                    </th>
                    <th
                      onClick={() => handleSort('updatedAt')}
                      style={{ width: 160, minWidth: 150 }}>
                      <SortableHeader>
                        Ngày cập nhật {renderSortIcon('updatedAt')}
                      </SortableHeader>
                    </th>
                    <th style={{ width: 110, minWidth: 100 }}>Chia sẻ với</th>
                    <th style={{ width: 140, minWidth: 120 }}>Chia sẻ bởi</th>
                    <th
                      style={{
                        width: 150,
                        minWidth: 150,
                        textAlign: 'right',
                      }}></th>
                  </tr>
                </thead>
                <tbody>
                  {/* Own items */}
                  {sortedItems.map(record => {
                    const isOwner = isItemOwner(record)
                    const owner = mentionUserList.find(
                      u => u.username === record.ownerUser
                    )

                    return (
                      <tr
                        key={record.id}
                        draggable={isOwner}
                        onDragStart={e => {
                          if (!isOwner) return
                          handleDragStart(e, record)
                        }}
                        onDragOver={e => handleDragOver(e, record)}
                        onDragLeave={handleDragLeave}
                        onDrop={e => handleDrop(e, record)}
                        style={{
                          background:
                            dragOverId === record.id ? '#e6f7ff' : undefined,
                          outline:
                            dragOverId === record.id
                              ? '2px dashed #1890ff'
                              : undefined,
                        }}>
                        <td>
                          <NameCell isFolder={record.type === 'folder'}>
                            {editingId === record.id ? (
                              <>
                                {record.type === 'folder' ? (
                                  <FolderOutlined
                                    style={{ fontSize: 18, color: '#faad14' }}
                                  />
                                ) : (
                                  <FileOutlined
                                    style={{ fontSize: 18, color: '#1890ff' }}
                                  />
                                )}
                                <InlineInput
                                  ref={renameInputRef}
                                  value={editingName}
                                  onChange={e => setEditingName(e.target.value)}
                                  onBlur={handleRenameConfirm}
                                  onKeyDown={handleRenameKeyDown}
                                  onClick={e => e.stopPropagation()}
                                />
                                {editingExtension && (
                                  <span
                                    style={{
                                      color: '#8c8c8c',
                                      userSelect: 'none',
                                      whiteSpace: 'nowrap',
                                    }}>
                                    {editingExtension}
                                  </span>
                                )}
                              </>
                            ) : record.type === 'folder' ? (
                              <span
                                className="doc-name"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 12,
                                  cursor: 'pointer',
                                  width: '100%',
                                  minWidth: 0,
                                }}
                                onClick={() => openFolder(record.id)}>
                                <FolderOutlined
                                  style={{
                                    fontSize: 18,
                                    color: '#faad14',
                                    flexShrink: 0,
                                  }}
                                />
                                <span
                                  style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={record.name}>
                                  {record.name}
                                </span>
                              </span>
                            ) : (
                              <>
                                <FileOutlined
                                  style={{
                                    fontSize: 18,
                                    color: '#1890ff',
                                    flexShrink: 0,
                                  }}
                                />
                                <FileItem
                                  key={record.id}
                                  file_id={record.gridFsFileId}
                                  file_name={`${record.name}`}
                                  file_type={utils.getExtensionFile(record.name)}
                                />
                              </>
                            )}
                          </NameCell>
                        </td>
                        <td>
                          <DateText>
                            {record.updatedAt
                              ? date_format.HH_mm_DD_MM_YYYY(record.updatedAt)
                              : '—'}
                          </DateText>
                        </td>
                        <td>
                          {record.sharedWith
                            ? renderSharedAvatars(
                                record.sharedWith,
                                record.ownerUser
                              )
                            : null}
                        </td>
                        <td>
                          {!isOwner && record.ownerUser ? (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                              }}>
                              <Avatar
                                size={22}
                                style={{
                                  backgroundColor: blue,
                                  fontSize: 11,
                                  flexShrink: 0,
                                }}
                                src={
                                  owner?.image &&
                                  `${apiUrl}/api/v1/images/${owner?.image}`
                                }>
                                {subStringAvatar(
                                  owner?.display || record.ownerUser
                                )}
                              </Avatar>
                              <DateText
                                style={{
                                  color: '#444',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                                title={owner?.display || record.ownerUser}>
                                {owner?.display || record.ownerUser}
                              </DateText>
                            </div>
                          ) : null}
                        </td>
                        <td>
                          {isOwner && (
                            <ActionCell>
                              {canShare && (
                                <Tooltip title="Chia sẻ">
                                  <ActionBtn
                                    onClick={() => handleClickShare(record)}>
                                    <ShareAltOutlined />
                                  </ActionBtn>
                                </Tooltip>
                              )}
                              {canRename && (
                                <Tooltip title="Đổi tên">
                                  <ActionBtn
                                    onClick={() => handleClickRename(record)}>
                                    <EditOutlined />
                                  </ActionBtn>
                                </Tooltip>
                              )}
                              {canMove && (
                                <Tooltip title="Kéo để di chuyển, hoặc click để chọn thư mục">
                                  <DragHandle
                                    draggable
                                    onDragStart={e => {
                                      e.stopPropagation()
                                      handleDragStart(e, record)
                                    }}
                                    onClick={() => handleClickMove(record)}>
                                    <HolderOutlined />
                                  </DragHandle>
                                </Tooltip>
                              )}
                              {canMove && (
                                <Tooltip
                                  title={
                                    record?.isPublished
                                      ? 'Huỷ công khai'
                                      : 'Công khai'
                                  }>
                                  <ActionBtn
                                    onClick={() => handleClickPublish(record)}>
                                    <ExpandAltOutlined />
                                  </ActionBtn>
                                </Tooltip>
                              )}
                              {canDelete && (
                                <Tooltip title="Xóa">
                                  <ActionBtn
                                    danger
                                    onClick={() =>
                                      handleClickDelete(record.id, record)
                                    }>
                                    <DeleteOutlined />
                                  </ActionBtn>
                                </Tooltip>
                              )}
                            </ActionCell>
                          )}
                        </td>
                      </tr>
                    )
                  })}

                  {/* Shared-with-me items — separator row */}
                  {!currentFolderId && sortedShareList.length > 0 && (
                    <tr>
                      <td colSpan={5}>
                        <SectionTitle style={{ margin: '12px 0 0' }}>
                          Được chia sẻ với tôi
                        </SectionTitle>
                      </td>
                    </tr>
                  )}

                  {/* Shared-with-me rows */}
                  {!currentFolderId &&
                    sortedShareList.map(record => {
                      const owner = mentionUserList.find(
                        u => u.username === record.ownerUser
                      )
                      return (
                        <tr key={`shared-${record.id}`}>
                          <td>
                            <NameCell isFolder={record.type === 'folder'}>
                              {record.type === 'folder' ? (
                                <span
                                  className="doc-name"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    cursor: 'pointer',
                                    width: '100%',
                                    minWidth: 0,
                                  }}
                                  onClick={() => openFolder(record.id)}>
                                  <FolderOutlined
                                    style={{
                                      fontSize: 18,
                                      color: '#faad14',
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span
                                    style={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                    title={record.name}>
                                    {record.name}
                                  </span>
                                </span>
                              ) : (
                                <>
                                  <FileOutlined
                                    style={{
                                      fontSize: 18,
                                      color: '#1890ff',
                                      flexShrink: 0,
                                    }}
                                  />
                                  <FileItem
                                    key={record.id}
                                    file_id={record.gridFsFileId}
                                    file_name={`${record.name}`}
                                    file_type={utils.getExtensionFile(
                                      record.name
                                    )}
                                  />
                                </>
                              )}
                            </NameCell>
                          </td>
                          <td>
                            <DateText>
                              {record.updatedAt
                                ? date_format.HH_mm_DD_MM_YYYY(record.updatedAt)
                                : '—'}
                            </DateText>
                          </td>
                          <td>
                            {record.sharedWith
                              ? renderSharedAvatars(
                                  record.sharedWith,
                                  record.ownerUser
                                )
                              : null}
                          </td>
                          <td>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                              }}>
                              <Avatar
                                size={22}
                                style={{
                                  backgroundColor: blue,
                                  fontSize: 11,
                                  flexShrink: 0,
                                }}
                                src={
                                  owner?.image &&
                                  `${apiUrl}/api/v1/images/${owner?.image}`
                                }>
                                {subStringAvatar(
                                  owner?.display || record.ownerUser
                                )}
                              </Avatar>
                              <DateText
                                style={{
                                  color: '#444',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                                title={owner?.display || record.ownerUser}>
                                {owner?.display || record.ownerUser}
                              </DateText>
                            </div>
                          </td>
                          <td />
                        </tr>
                      )
                    })}
                </tbody>
              </FileTable>
            </FileTableWrapper>
          )}
        </ContentBlockWrapper>
      </DashboardLayout>

      <CreateMyFolderModal
        visible={showCreateFolderModal}
        parentFolderId={resourcesStore.currentFolderId}
        onClose={() => setShowCreateFolderModal(false)}
      />
      <UploadMyFileModal
        visible={showUploadFileModal}
        parentFolderId={resourcesStore.currentFolderId}
        onClose={() => setShowUploadFileModal(false)}
      />

      <MoveDocumentModal
        visible={showMoveDocumentModal}
        currentDocument={toJS(currentDocument)}
        parentFolderId={currentFolderId}
        onClose={() => setShowMoveDocumentModal(false)}
      />
      <ShareDocumentModal
        visible={showShareDocumentModal}
        currentDocument={toJS(currentDocument)}
        parentFolderId={currentFolderId}
        onClose={() => setShowShareDocumentModal(false)}
      />
    </>
  )
}

MyDocumentPage.propTypes = {}

export default inject(
  'resourcesStore',
  'aclStore',
  'authenticationStore',
  'userStore'
)(observer(MyDocumentPage))
